import axios from 'axios';
const API_URL = process.env.VUE_APP_API_URL;

class DashboardService {
  async list() {
    try {
      const { data } = await axios.get(API_URL + '/dashboards');
      return data;
    } catch (error) {
      if (error.response) {
        if (error.response.status === 401) {
          return { status: 'error', message: 'Unauthorized', data: [] };
        }
      }
      return { status: 'error', message: 'Failed to fetch dashboards', data: [] };
    }
  }

  async get(id) {
    try {
      const { data } = await axios.get(API_URL + `/dashboards/${id}`);
      return data;
    } catch (error) {
      if (error.response) {
        if (error.response.status === 401) {
          return { status: 'error', message: 'Unauthorized', data: null };
        } else if (error.response.status === 403) {
          return { status: 'error', message: error.response.data?.message || 'Access denied', data: null };
        } else if (error.response.status === 404) {
          return { status: 'error', message: 'Dashboard not found', data: null };
        }
      }
      return { status: 'error', message: 'Failed to fetch dashboard', data: null };
    }
  }

  async create(payload) {
    try {
      const { data } = await axios.post(API_URL + '/dashboards', payload);
      return data;
    } catch (error) {
      if (error.response) {
        if (error.response.status === 401) {
          return { status: 'error', message: 'Unauthorized', data: null };
        } else if (error.response.status === 400) {
          return {
            status: 'error',
            message: error.response.data.message || 'Invalid request',
            data: null
          };
        }
      }
      return { status: 'error', message: 'Failed to create dashboard', data: null };
    }
  }

  async update(id, payload) {
    try {
      const { data } = await axios.put(API_URL + `/dashboards/${id}`, payload);
      return data;
    } catch (error) {
      if (error.response) {
        if (error.response.status === 401) {
          return { status: 'error', message: 'Unauthorized', data: null };
        } else if (error.response.status === 404) {
          return { status: 'error', message: 'Dashboard not found', data: null };
        } else if (error.response.status === 400) {
          return {
            status: 'error',
            message: error.response.data.message || 'Invalid request',
            data: null
          };
        }
      }
      return { status: 'error', message: 'Failed to update dashboard', data: null };
    }
  }

  async delete(id, options = {}) {
    try {
      const { data } = await axios.delete(API_URL + `/dashboards/${id}`, { data: options });
      return data;
    } catch (error) {
      if (error.response) {
        if (error.response.status === 401) {
          return { status: 'error', message: 'Unauthorized', data: null };
        } else if (error.response.status === 404) {
          return { status: 'error', message: 'Dashboard not found', data: null };
        }
      }
      return { status: 'error', message: 'Failed to delete dashboard', data: null };
    }
  }

  async getSavedSearches(id) {
    try {
      const { data } = await axios.get(API_URL + `/dashboards/${id}/saved-searches`);
      return data;
    } catch (error) {
      if (error.response?.status === 401) {
        return { status: 'error', message: 'Unauthorized', data: [] };
      }
      if (error.response?.status === 404) {
        return { status: 'error', message: 'Dashboard not found', data: [] };
      }
      return { status: 'error', message: 'Failed to get saved searches', data: [] };
    }
  }

  async addWidget(dashboardId, widgetData) {
    try {
      const { data } = await axios.post(
        API_URL + `/dashboards/${dashboardId}/widgets`,
        widgetData
      );
      return data;
    } catch (error) {
      if (error.response) {
        if (error.response.status === 401) {
          return { status: 'error', message: 'Unauthorized', data: null };
        } else if (error.response.status === 404) {
          return { status: 'error', message: 'Dashboard not found', data: null };
        } else if (error.response.status === 400) {
          return {
            status: 'error',
            message: error.response.data.message || 'Invalid request',
            data: null
          };
        }
      }
      return { status: 'error', message: 'Failed to add widget', data: null };
    }
  }

  async updateWidget(dashboardId, widgetId, updates) {
    try {
      const { data } = await axios.put(
        API_URL + `/dashboards/${dashboardId}/widgets/${widgetId}`,
        updates
      );
      return data;
    } catch (error) {
      if (error.response) {
        if (error.response.status === 401) {
          return { status: 'error', message: 'Unauthorized', data: null };
        } else if (error.response.status === 404) {
          return { status: 'error', message: 'Widget not found', data: null };
        } else if (error.response.status === 400) {
          return {
            status: 'error',
            message: error.response.data.message || 'Invalid request',
            data: null
          };
        }
      }
      return { status: 'error', message: 'Failed to update widget', data: null };
    }
  }

  async deleteWidget(dashboardId, widgetId) {
    try {
      const { data } = await axios.delete(
        API_URL + `/dashboards/${dashboardId}/widgets/${widgetId}`
      );
      return data;
    } catch (error) {
      if (error.response) {
        if (error.response.status === 401) {
          return { status: 'error', message: 'Unauthorized', data: null };
        } else if (error.response.status === 404) {
          return { status: 'error', message: 'Widget not found', data: null };
        }
      }
      return { status: 'error', message: 'Failed to delete widget', data: null };
    }
  }

  async browse() {
    try {
      const { data } = await axios.get(API_URL + '/dashboards/browse');
      return data;
    } catch (error) {
      if (error.response?.status === 401) {
        return { status: 'error', message: 'Unauthorized', data: [] };
      }
      return { status: 'error', message: 'Failed to browse dashboards', data: [] };
    }
  }

  async importDashboard(exportData) {
    try {
      const { data } = await axios.post(API_URL + '/dashboards/import', exportData);
      return data;
    } catch (error) {
      if (error.response?.status === 401) {
        return { status: 'error', message: 'Unauthorized', data: null };
      }
      if (error.response?.status === 400) {
        return { status: 'error', message: error.response.data.message || 'Invalid import data', data: null };
      }
      return { status: 'error', message: 'Failed to import dashboard', data: null };
    }
  }

  async exportDashboard(id) {
    try {
      const { data } = await axios.get(API_URL + `/dashboards/${id}/export`);
      return data;
    } catch (error) {
      if (error.response?.status === 401) {
        return { status: 'error', message: 'Unauthorized', data: null };
      }
      if (error.response?.status === 404) {
        return { status: 'error', message: 'Dashboard not found', data: null };
      }
      return { status: 'error', message: 'Failed to export dashboard', data: null };
    }
  }

  async subscribe(id) {
    try {
      const { data } = await axios.post(API_URL + `/dashboards/${id}/subscribe`);
      return data;
    } catch (error) {
      if (error.response?.status === 401) {
        return { status: 'error', message: 'Unauthorized', data: null };
      }
      if (error.response?.status === 404) {
        return { status: 'error', message: 'Dashboard not found', data: null };
      }
      return { status: 'error', message: 'Failed to subscribe', data: null };
    }
  }

  async unsubscribe(id) {
    try {
      const { data } = await axios.delete(API_URL + `/dashboards/${id}/subscribe`);
      return data;
    } catch (error) {
      if (error.response?.status === 401) {
        return { status: 'error', message: 'Unauthorized', data: null };
      }
      if (error.response?.status === 404) {
        return { status: 'error', message: 'Dashboard not found', data: null };
      }
      return { status: 'error', message: 'Failed to unsubscribe', data: null };
    }
  }

  async fork(id, options = {}) {
    try {
      const { data } = await axios.post(API_URL + `/dashboards/${id}/fork`, options);
      return data;
    } catch (error) {
      if (error.response?.status === 401) {
        return { status: 'error', message: 'Unauthorized', data: null };
      }
      if (error.response?.status === 403 && error.response.data?.code === 'fork_required') {
        return { status: 'error', code: 'fork_required', message: error.response.data.message || 'Fork required', data: null };
      }
      if (error.response?.status === 404) {
        return { status: 'error', message: 'Dashboard not found', data: null };
      }
      return { status: 'error', message: 'Failed to fork dashboard', data: null };
    }
  }

  async setVisibility(id, visibility) {
    try {
      const { data } = await axios.put(API_URL + `/dashboards/${id}/visibility`, { visibility });
      return data;
    } catch (error) {
      if (error.response?.status === 401) {
        return { status: 'error', message: 'Unauthorized', data: null };
      }
      if (error.response?.status === 404) {
        return { status: 'error', message: 'Dashboard not found', data: null };
      }
      return { status: 'error', message: 'Failed to update visibility', data: null };
    }
  }

  async updateLayout(dashboardId, layouts) {
    try {
      const { data } = await axios.put(
        API_URL + `/dashboards/${dashboardId}/layout`,
        { layouts }
      );
      return data;
    } catch (error) {
      if (error.response) {
        if (error.response.status === 401) {
          return { status: 'error', message: 'Unauthorized', data: null };
        } else if (error.response.status === 404) {
          return { status: 'error', message: 'Dashboard not found', data: null };
        }
      }
      return { status: 'error', message: 'Failed to update layouts', data: null };
    }
  }
}

export default new DashboardService();
